/**
 * JS Object editor pane (WS-2) — the FULL-HEIGHT editor for ONE page JS Object,
 * shown in the main workspace when a JS tab is active. Lifted out of the bottom
 * dock's cramped strip (a JS module needs vertical room). A JS Object is the
 * page's single home for authored CLIENT logic — `export default { fn(args){…} }`
 * whose sync functions are callable from bindings as `{{ <Name>.fn(args) }}`;
 * they get their data as ARGUMENTS, never touching the DB (that stays a Query).
 *
 * The layout MIRRORS the app-Query Logic editor (`AppQueriesPane`) so the two
 * code surfaces read the same (owner's "deve ricalcare quella delle query app
 * logic"): a slim IDENTITY header (name · kind badge · Elimina), a CENTRAL code
 * HERO (`CodeField fill`), and a RIGHT RAIL with the exported-function roster +
 * the "Prova funzione" tester. NB the tester runs CLIENT-SIDE (the real engine
 * JS Objects run in — `previewExpr` over the live preview scope); a server-side
 * breakpoint debugger like the Logic one is a deliberate future decision (JS
 * Objects execute in the browser, not the server QuickJS the Logic debugger
 * steps).
 *
 * Name validation MIRRORS the backend (`hub.apps.pages`): a JS identifier,
 * unique across the page, no `$`-prefix, not a reserved global.
 */
import { Play, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { compileJsObject } from "@/lib/pageJsObjects";
import type { ExprPreview } from "@/lib/pagePreview";
import type { PageJsObject } from "@/lib/pageTypes";

import { CodeField } from "./CodeField";

/** Ambient TS for the JS Object editor — the data the functions can reach (via
 *  their arguments) + a reminder they don't touch the DB. */
const JS_OBJECT_DTS = `
// A JS Object holds the page's CLIENT logic. Export an object of functions:
//   export default { fullName(row) { return row.first + ' ' + row.last } }
// SYNC functions are callable from bindings: {{ Utils.fullName($item) }}.
// They get their DATA as arguments — they do NOT read the database directly
// (data access stays a Query; call a function with $queries.<name>.rows / $item).
declare const console: { log(...args: any[]): void };
`;

// Shared field styling — mirrors AppQueriesPane's constants so the two editors
// read identically (kept local; those aren't exported).
const fieldCls = "rounded border border-border bg-background px-1.5 py-1 text-[12px]";
const labelCls = "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70";

const NAME_RE = /^[A-Za-z_][\w$]*$/;
const RESERVED = new Set([
  "Math",
  "JSON",
  "Object",
  "Array",
  "Number",
  "String",
  "Boolean",
  "parseInt",
  "parseFloat",
  "isNaN",
  "isFinite",
]);

/** Validate a JS Object name against the page's other names (uniqueness) + the
 *  identifier/reserved rules — mirrors the backend. */
function jsObjectNameError(name: string, siblingNames: string[]): string | null {
  if (!NAME_RE.test(name)) return "Identificatore JS non valido (niente $ iniziale)";
  if (RESERVED.has(name)) return `"${name}" è un nome riservato`;
  if (siblingNames.includes(name)) return "Nome duplicato";
  return null;
}

export function JsObjectEditorPane({
  jsObject: obj,
  siblingNames,
  onChange,
  onRemove,
  previewExpr,
}: {
  jsObject: PageJsObject;
  /** Names of the page's OTHER JS Objects, for the uniqueness check. */
  siblingNames: string[];
  onChange: (next: PageJsObject) => void;
  onRemove: () => void;
  previewExpr?: (tpl: string) => ExprPreview | null;
}) {
  const error = jsObjectNameError(obj.name, siblingNames);
  // Compile the module so we can show what it EXPORTS (the function roster) + a
  // compile error — making the JS Object as inspectable as a Query (ADR-0044 U3).
  const compiled = useMemo(() => compileJsObject(obj.code), [obj.code]);
  const compileError = "error" in compiled ? compiled.error : null;
  const fnNames = useMemo(() => {
    if (!("ns" in compiled)) return [];
    return Object.keys(compiled.ns).filter((k) => typeof compiled.ns[k] === "function");
  }, [compiled]);

  // Layout: a slim IDENTITY header (name · kind badge · Elimina), then a body
  // split into the CENTRAL code hero + a right rail (Funzioni · Prova) — the
  // mirror of AppQueriesPane's Logic detail.
  return (
    <div className="flex h-full min-h-0 flex-col text-[12px]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border/60 px-3 py-2">
        <input
          aria-label="JS Object name"
          className={`min-w-0 max-w-[220px] flex-1 rounded border bg-transparent px-1 py-0.5 text-sm font-semibold text-foreground focus:border-accent focus:outline-none ${
            error ? "border-danger" : "border-transparent hover:border-border"
          }`}
          value={obj.name}
          placeholder="Nome oggetto"
          onChange={(e) => onChange({ ...obj, name: e.target.value })}
        />
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          JS Object
        </span>
        <button
          type="button"
          aria-label="Remove JS Object"
          title="Elimina"
          className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-danger hover:bg-danger/10"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>

      {error && <p className="mx-3 mt-2 shrink-0 text-[11px] text-danger">{error}</p>}

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* CENTRAL surface — the code IS the hero (fills the pane). */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3">
          <div className="mb-1.5 flex items-center justify-between">
            <span className={labelCls}>Codice · JS Object (client)</span>
          </div>
          <CodeField
            fill
            label="Codice"
            value={obj.code}
            onChange={(code) => onChange({ ...obj, code })}
            ambientDts={JS_OBJECT_DTS}
            placeholder="export default { fn(row) { return row.x } }"
          />
          <p className="mt-1 shrink-0 text-[11px] text-muted-foreground">
            export default {"{ … }"} — le funzioni sync sono richiamabili dai binding:{" "}
            <code className="font-mono">{"{{ "}{obj.name || "Nome"}.fn(){" }}"}</code>.
          </p>
        </div>

        {/* RIGHT RAIL — the exported-function roster + the tester. */}
        <div className="flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-t border-border/60 p-3 lg:w-[340px] lg:border-l lg:border-t-0">
          {/* Function roster — what this object exports (the JS-Object twin of a
              Query's columns). A compile error replaces it. */}
          <div className="rounded-md border border-border/60">
            <div className="px-2.5 py-2">
              <span className={labelCls}>Funzioni</span>
            </div>
            <div className="border-t border-border/60 p-2.5">
              {compileError ? (
                <p className="text-[11px] text-danger">Errore di compilazione: {compileError}</p>
              ) : fnNames.length === 0 ? (
                <span className="text-[11px] text-muted-foreground">nessuna</span>
              ) : (
                <div className="flex flex-wrap items-center gap-1">
                  {fnNames.map((fn) => (
                    <code key={fn} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                      {obj.name}.{fn}()
                    </code>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* "Prova funzione" — run a sync function against the LIVE preview scope
              and see the return, like a Query's "Esegui" + result (ADR-0044 U3).
              Keyed by the object name so its transient state resets on switch. */}
          {previewExpr && !compileError && fnNames.length > 0 && (
            <FunctionTester
              key={obj.name}
              objName={obj.name}
              fnNames={fnNames}
              previewExpr={previewExpr}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Run `<Object>.<fn>(<args>)` against the live preview scope and show the result. */
function FunctionTester({
  objName,
  fnNames,
  previewExpr,
}: {
  objName: string;
  fnNames: string[];
  previewExpr: (tpl: string) => ExprPreview | null;
}) {
  const [fn, setFn] = useState(fnNames[0]);
  const [args, setArgs] = useState("");
  const [result, setResult] = useState<ExprPreview | null>(null);
  const pickedFn = fnNames.includes(fn) ? fn : fnNames[0];

  const run = () => {
    setResult(previewExpr(`{{ ${objName}.${pickedFn}(${args}) }}`));
  };

  return (
    <div className="rounded-md border border-border/60">
      <div className="flex items-center justify-between px-2.5 py-2">
        <span className={labelCls}>Prova funzione</span>
        <button
          type="button"
          onClick={run}
          className="flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2.5 py-1 text-[11px] font-medium text-accent hover:bg-accent/20"
        >
          <Play className="h-3 w-3" strokeWidth={1.75} /> Esegui
        </button>
      </div>
      <div className="flex flex-col gap-1.5 border-t border-border/60 p-2.5">
        <div className="flex items-center gap-1.5">
          <select
            aria-label="Funzione da provare"
            className={`${fieldCls} font-mono`}
            value={pickedFn}
            onChange={(e) => setFn(e.target.value)}
          >
            {fnNames.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <span className="font-mono text-xs text-muted-foreground">(</span>
          <input
            aria-label="Argomenti"
            className={`${fieldCls} min-w-0 flex-1 font-mono`}
            placeholder="es. $queries.recent.data"
            value={args}
            onChange={(e) => setArgs(e.target.value)}
          />
          <span className="font-mono text-xs text-muted-foreground">)</span>
        </div>
        {result &&
          (result.ok ? (
            <pre className="overflow-x-auto rounded bg-muted px-2 py-1 font-mono text-[11px] text-foreground">
              = {result.display}
            </pre>
          ) : (
            <p className="text-[11px] text-danger">{result.error}</p>
          ))}
      </div>
    </div>
  );
}
